import { Injectable } from '@angular/core';
import {
  InsightsProducts,
  InsightsProductsFilter,
} from '@order/pages/order-insights/models/insights-products.model';
import { Product } from '@shared/models/product.model';
import { Order } from '@usf/ngrx-order';
import { TranslateService } from '@ngx-translate/core';
import { OrderItem } from '@usf/ngrx-order';
import {
  OrderInsightData,
  OrderInsightsType,
  OrderInsightsViewModel,
} from '@shared/models/order-insights.model';
import {
  OrderCalculation,
  OrderCalculationsDisplayed,
} from '@shared/models/calculations-displayed-model';
import { PanAppState } from '@panamax/app-state';
import { Observable, combineLatest, of, take } from 'rxjs';
import { map } from 'rxjs/operators';
import { FEATURES } from '@shared/constants/splitio-features';

@Injectable({
  providedIn: 'root',
})
export class OrderInsightsPageHeaderService {
  public sgpTotal = 0;
  public dollarToTargetTotal = 0;

  constructor(
    private translateService: TranslateService,
    private panAppState: PanAppState,
  ) {}

  /**
   * Loads and filters percentages display data based on visibility and feature flags
   * @param viewModel - The order insights view model
   * @param order - The current order
   * @param dynamicCookbookOrderFeatureFlag - Cookbook feature flag name
   * @param dynamicPmeSgpFlag - PME SGP feature flag name
   * @param dynamicOrderInsightsPmeSgpFlag - Order insights PME SGP feature flag name
   * @returns Observable that emits the filtered calculations
   */
  loadPercentagesDisplayed(
    order: Order,
    dynamicCookbookOrderFeatureFlag: string,
    dynamicPmeSgpFlag: string,
    dynamicOrderInsightsPmeSgpFlag: string,
  ): Observable<OrderCalculationsDisplayed> {
    const allCalculations = [
      {
        calculatedValue: order.orderHeader?.cookbookAdoptionPercent,
        title: this.translateService.instant(
          'i18n.orderInsightsPage.cookbookAdoptionTitle',
        ),
        displayFlags: [dynamicCookbookOrderFeatureFlag],
        dataCy: 'cookbook-adoption',
        percentage: true,
        visible: order.orderHeader?.cookbookAdoptionPercent > 0,
      } as OrderCalculation,
      {
        calculatedValue: this.sgpTotal,
        title: this.translateService.instant('i18n.orderInsightsPage.sgpTitle'),
        displayFlags: [dynamicPmeSgpFlag, dynamicOrderInsightsPmeSgpFlag],
        dataCy: 'sgp-amount',
        dollarAmount: true,
        visible: true,
      } as OrderCalculation,
      {
        calculatedValue: this.dollarToTargetTotal,
        title: this.translateService.instant(
          'i18n.orderInsightsPage.dollarToTargetTitle',
        ),
        displayFlags: [dynamicPmeSgpFlag, dynamicOrderInsightsPmeSgpFlag],
        dataCy: 'dollars-to-target',
        dollarAmount: true,
        visible: this.dollarToTargetTotal < 0,
      } as OrderCalculation,
    ] as OrderCalculation[];

    // Filter calculations based on visibility first
    const visibleCalculations = allCalculations.filter(item => item?.visible);

    // Create feature flag observables for each calculation
    const featureFlagChecks = visibleCalculations.map(item => {
      if (!item?.displayFlags || item.displayFlags.length === 0) {
        return of(true); // If no displayFlags, always include
      }
      // Check if all feature flags for this item are enabled
      return this.panAppState.feature$(item.displayFlags, true);
    });

    // Combine all feature flag checks and return the filtered calculations
    if (featureFlagChecks.length > 0) {
      return combineLatest(featureFlagChecks).pipe(
        take(1),
        map(flagResults => {
          const filteredCalculations = visibleCalculations.filter(
            (item, index) => flagResults[index],
          );
          return {
            lineDivider: true,
            calculations: filteredCalculations,
          } as OrderCalculationsDisplayed;
        }),
      );
    } else {
      return of({
        lineDivider: true,
        calculations: [],
      } as OrderCalculationsDisplayed);
    }
  }

  /**
   * Filters order insights by type in a single pass
   * @param insightInformation - Map of order insights data
   * @returns Object containing filtered Maps for lost lines and new products ordered
   */
  filterOrderInsightsByType(
    insightInformation: Map<number, OrderInsightData>,
  ): {
    lostLines: Map<number, Product>;
    newProductsOrdered: Map<number, Product>;
  } {
    const lostLines = new Map<number, Product>();
    const newProductsOrdered = new Map<number, Product>();

    insightInformation.forEach((insightData, productNumber) => {
      if (insightData.orderInsightType === OrderInsightsType.LostLines) {
        lostLines.set(productNumber, insightData?.product);
      } else if (
        insightData.orderInsightType === OrderInsightsType.NewProductsOrdered
      ) {
        newProductsOrdered.set(productNumber, insightData?.product);
      }
    });

    return { lostLines, newProductsOrdered };
  }

  setSgpTotal(orderedItem: OrderItem, product: Product) {
    const grossProfitDollars = orderedItem?.grossProfitDollars ?? 0;
    const dollarToTarget = orderedItem?.dollarToTarget ?? 0;
    if (orderedItem?.priceUOM.toUpperCase() === 'LB') {
      const netWeight = product?.summary?.netWeight ?? 0;
      this.sgpTotal += netWeight * grossProfitDollars;
      this.dollarToTargetTotal += dollarToTarget * netWeight;
    } else {
      this.sgpTotal += grossProfitDollars;
      this.dollarToTargetTotal += dollarToTarget;
    }
  }

  getLostLinesProducts(productMap: Map<number, Product>): InsightsProducts {
    const filteredProducts = [...productMap.values()];

    return {
      color: 'red',
      badgeColor: 'red-badge',
      products: filteredProducts,
      badgeCount: filteredProducts.length,
      count: filteredProducts.length,
      title: 'Lost Lines',
    };
  }

  getNewProductsOrderedProducts(
    productMap: Map<number, Product>,
  ): InsightsProducts {
    const filteredProducts = [...productMap.values()];

    return {
      color: 'green',
      badgeColor: 'green-badge',
      products: filteredProducts,
      badgeCount: filteredProducts.length,
      count: filteredProducts.length,
      title: 'New Products Ordered',
    };
  }

  getProductsInOrder(
    order: Order,
    productMap: Map<number, Product>,
  ): InsightsProducts {
    let products: Product[] = [];
    this.sgpTotal = 0;
    this.dollarToTargetTotal = 0;

    order?.orderItems?.forEach(orderItem => {
      const product = productMap.get(orderItem?.productNumber);
      this.setSgpTotal(orderItem, product);
      products.push(product);
    });

    return {
      color: 'black',
      badgeColor: 'black-badge',
      products: products,
      badgeCount: products.length,
      count: products.length,
      title: 'Products in Order',
    };
  }

  getQuickFilters(
    lostLinesProducts: InsightsProducts,
    newProductsOrderedProducts: InsightsProducts,
    productsInOrder: InsightsProducts,
  ): InsightsProductsFilter[] {
    const allProducts = [
      lostLinesProducts,
      newProductsOrderedProducts,
      productsInOrder,
    ];
    const totalCount = allProducts.reduce(
      (sum, group) => sum + (group.products?.length || 0),
      0,
    );

    return [
      {
        id: 'all',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.allProducts',
        ),
        selected: true,
        insightsProducts: allProducts,
        badgeCount: 1,
        count: totalCount,
      },
      {
        id: 'lost-lines',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.lostLines',
        ),
        color: 'red',
        badgeColor: 'red-badge',
        badgeCount: lostLinesProducts.products.length,
        count: lostLinesProducts.products.length,
        selected: false,
        insightsProducts: [lostLinesProducts],
      },
      {
        id: 'new-products-ordered',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.newProductsOrdered',
        ),
        color: 'green',
        badgeColor: 'green-badge',
        badgeCount: newProductsOrderedProducts.products.length,
        count: newProductsOrderedProducts.products.length,
        selected: false,
        insightsProducts: [newProductsOrderedProducts],
      },
      {
        id: 'products-in-order',
        title: this.translateService.instant(
          'i18n.orderInsightsPage.tabs.productsInOrder',
        ),
        badgeColor: 'black-badge',
        badgeCount: productsInOrder.products.length,
        count: productsInOrder.products.length,
        selected: false,
        insightsProducts: [productsInOrder],
      },
    ];
  }
}
